import * as React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRouter } from 'next/router';
import { useNoSsrCodeVariant } from 'docs/src/modules/utils/codeVariant';
import { useUserLanguage } from '@mui/docs/i18n';
import { pathnameToLanguage } from 'docs/src/modules/utils/helpers';
import { ThemeOptionsContext } from 'docs/src/modules/components/ThemeContext';

// So we can write code like:
//
// <Button
//   data-ga-event-category="demo"
//   data-ga-event-action="expand"
// >
//   Foo
// </Button>
function handleClick(event) {
  var element = event.target;
  while (element && element !== document) {
    var category = element.getAttribute('data-ga-event-category');

    // We reach a tracking element, no need to look higher in the dom tree.
    if (category) {
      var split = parseFloat(element.getAttribute('data-ga-event-split'));
      if (split && split < Math.random()) {
        return;
      }
      window.gtag('event', category, {
        eventAction: element.getAttribute('data-ga-event-action'),
        eventLabel: element.getAttribute('data-ga-event-label')
      });
      break;
    }
    element = element.parentElement;
  }
}
var boundDataGaListener = false;

/**
 * basically just a `useAnalytics` hook.
 * However, it needs the redux store which is created
 * in the same component this "hook" is used.
 */
function GoogleAnalytics() {
  React.useEffect(function () {
    if (!boundDataGaListener) {
      boundDataGaListener = true;
      document.addEventListener('click', handleClick);
    }
  }, []);
  var router = useRouter();
  var timeout = React.useRef();
  React.useEffect(function () {
    // Wait for the title to be updated.
    // React fires useEffect twice in dev mode
    clearTimeout(timeout.current);
    timeout.current = setTimeout(function () {
      var _pathnameToLanguage = pathnameToLanguage(window.location.pathname),
        canonicalAsServer = _pathnameToLanguage.canonicalAsServer;

      // https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: canonicalAsServer,
        productId: document.querySelector('meta[name="mui:productId"]').content,
        productCategoryId: document.querySelector('meta[name="mui:productCategoryId"]').content
      });
    });
  }, [router.route]);
  var codeVariant = useNoSsrCodeVariant();
  React.useEffect(function () {
    window.gtag('set', 'user_properties', {
      codeVariant: codeVariant
    });
  }, [codeVariant]);
  var userLanguage = useUserLanguage();
  React.useEffect(function () {
    window.gtag('set', 'user_properties', {
      userLanguage: userLanguage
    });
  }, [userLanguage]);
  React.useEffect(function () {
    /**
     * Based on https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio#Monitoring_screen_resolution_or_zoom_level_changes
     * Adjusted to track 3 or more different ratios
     */
    function trackDevicePixelRation() {
      var devicePixelRatio = Math.round(window.devicePixelRatio * 10) / 10;
      window.gtag('set', 'user_properties', {
        devicePixelRatio: devicePixelRatio
      });
    }
    trackDevicePixelRation();

    /**
     * @type {MediaQueryList}
     */
    var matchMedia = window.matchMedia("(resolution: ".concat(window.devicePixelRatio, "dppx)"));
    // Intentionally use deprecated listener methods to support iOS & old browsers
    matchMedia.addListener(trackDevicePixelRation);
    return function () {
      matchMedia.removeListener(trackDevicePixelRation);
    };
  }, []);
  var prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)', {
    noSsr: true
  });
  var colorSchemeOS = prefersDarkMode ? 'dark' : 'light';
  var themeOptions = React.useContext(ThemeOptionsContext);
  var colorScheme = themeOptions.paletteMode;
  React.useEffect(function () {
    window.gtag('set', 'user_properties', {
      colorSchemeOS: colorSchemeOS
    });
  }, [colorSchemeOS]);
  React.useEffect(function () {
    window.gtag('set', 'user_properties', {
      colorScheme: colorScheme
    });
  }, [colorScheme]);
  return null;
}
export default /*#__PURE__*/React.memo(GoogleAnalytics);